import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, Clock, Fuel, MapPin } from 'lucide-react';
import CTABanner from '../../components/CTABanner';
import { usePageMeta, useStructuredData } from '../../lib/seo';
import { SITE_URL } from '../../config/productFacts';
import CITIES from '../../data/tankstellenCities.json';
import { FUELS, FuelTabs, StationList, DataAttribution, fmtPrice } from './tankstellenShared';

const API = process.env.REACT_APP_BACKEND_URL;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_EMBED_KEY;

export default function TankstellenCityPage() {
  const { slug } = useParams();
  const city = useMemo(() => CITIES.find((c) => c.slug === slug), [slug]);
  const name = city?.name || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fuel, setFuel] = useState('e5');
  const [showAll, setShowAll] = useState(false);

  usePageMeta(
    `Tankstellen in ${name}: Aktuelle Benzinpreise & Diesel | FuelX`,
    `Alle Spritpreise für ${name} im Live-Vergleich: Super E5, E10 & Diesel. Jetzt die günstigste Tankstelle in ${name}${city?.bundesland ? ` (${city.bundesland})` : ''} finden — alle 15 Minuten aktualisiert.`,
    { canonical: `${SITE_URL}/tankstellen/${slug}` }
  );

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setLoading(true);
    setData(null);
    setShowAll(false);
    axios
      .get(`${API}/api/tankstellen/city/${slug}`)
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData({ status: 'error', stations: [], stats: {}, neighbors: [] }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug]);

  const schema = useMemo(() => {
    if (!city) return null;
    const graph = [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'FuelX', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Tankstellen', item: `${SITE_URL}/tankstellen` },
          { '@type': 'ListItem', position: 3, name: `Tankstellen in ${city.name}`, item: `${SITE_URL}/tankstellen/${city.slug}` },
        ],
      },
    ];
    if (data?.stations?.length) {
      graph.push({
        '@type': 'ItemList',
        name: `Tankstellen in ${city.name}`,
        numberOfItems: data.station_count || data.stations.length,
        itemListElement: data.stations.slice(0, 10).map((s, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'GasStation',
            name: s.brand || s.name || 'Tankstelle',
            address: {
              '@type': 'PostalAddress',
              streetAddress: `${s.street} ${s.house_number}`.trim(),
              postalCode: s.postcode,
              addressLocality: s.city || city.name,
              addressCountry: 'DE',
            },
          },
        })),
      });
    }
    return { '@graph': graph };
  }, [city, data]);
  useStructuredData(schema, `tankstellen-${slug}`);

  if (!city) return <Navigate to="/tankstellen" replace />;

  const stats = data?.stats || {};
  const national = data?.national || {};
  const activeStat = stats[fuel];
  const fuelLabel = FUELS.find((f) => f.key === fuel)?.label || '';
  const spreadCt = activeStat ? Math.round((activeStat.max - activeStat.min) * 100) : null;

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid="tankstellen-city-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-12 sm:pt-16 pb-16">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12px] text-stone-400 mb-8" data-testid="city-breadcrumb">
          <Link to="/" className="hover:text-stone-900 transition-colors">FuelX</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={2} />
          <Link to="/tankstellen" className="hover:text-stone-900 transition-colors">Tankstellen</Link>
          <ChevronRight className="w-3 h-3" strokeWidth={2} />
          <span className="text-stone-700">{name}</span>
        </nav>

        <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-700 mb-5">
          {city.bundesland ? `${city.bundesland} · ` : ''}Live-Spritpreise
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.03em] text-stone-900" data-testid="city-h1">
          Tankstellen in {name}: Aktuelle Spritpreise
        </h1>
        <p className="text-lg text-stone-600 leading-[1.7] mt-6 max-w-2xl" data-testid="city-intro">
          {data?.station_count
            ? <>Live-Preisvergleich für <strong className="text-stone-900">{data.station_count} Tankstellen</strong> in und um {name} ({data.open_count} aktuell geöffnet). </>
            : <>Live-Preisvergleich für alle Tankstellen in und um {name}. </>}
          Die Daten stammen aus der amtlichen Markttransparenzstelle für Kraftstoffe und werden alle 15 Minuten aktualisiert.
        </p>

        {/* Preis-Statistik-Karten */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-10" data-testid="city-price-stats">
          {FUELS.map((f) => {
            const st = stats[f.key];
            const nat = national[f.key];
            const deltaCt = st && typeof nat === 'number' ? Math.round((st.avg - nat) * 100) : null;
            return (
              <div key={f.key} className={`rounded-2xl border p-5 transition-colors ${fuel === f.key ? 'border-stone-900 bg-stone-950 text-white' : 'border-stone-200 bg-white'}`} data-testid={`price-stat-${f.key}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${fuel === f.key ? 'text-emerald-400' : 'text-stone-400'}`}>{f.label} · günstigster Preis</p>
                <p className={`mt-2 text-3xl font-medium tabular-nums tracking-tight ${fuel === f.key ? 'text-white' : 'text-stone-900'}`}>
                  {loading ? '···' : fmtPrice(st?.min)}
                </p>
                <p className={`mt-1.5 text-[12px] ${fuel === f.key ? 'text-stone-400' : 'text-stone-500'}`}>
                  Ø {name}: {fmtPrice(st?.avg)}
                  {deltaCt !== null && (
                    <span className={`ml-2 font-semibold ${deltaCt <= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                      {deltaCt <= 0 ? `${Math.abs(deltaCt)} ct unter` : `${deltaCt} ct über`} DE-Ø
                    </span>
                  )}
                </p>
              </div>
            );
          })}
        </div>

        {/* Günstigste Station Highlight */}
        {activeStat?.cheapest_station && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="cheapest-highlight">
            <Fuel className="w-5 h-5 shrink-0 text-emerald-700" strokeWidth={1.5} />
            <p className="text-[14px] text-stone-700">
              <strong className="text-stone-900">Günstigster {fuelLabel} jetzt:</strong>{' '}
              {activeStat.cheapest_station.brand || activeStat.cheapest_station.name}
              {activeStat.cheapest_station.street ? `, ${activeStat.cheapest_station.street}` : ''} — <strong className="text-emerald-700 tabular-nums">{fmtPrice(activeStat.min)}</strong>
            </p>
          </div>
        )}

        {/* Stationen-Liste */}
        <div className="mt-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900">
              Die günstigsten Tankstellen in {name}
            </h2>
            <FuelTabs fuel={fuel} onChange={setFuel} />
          </div>
          {loading ? (
            <div className="rounded-2xl border border-stone-200 divide-y divide-stone-100 overflow-hidden animate-pulse" data-testid="city-loading">
              {[...Array(6)].map((_, i) => <div key={i} className="h-16 bg-stone-50" />)}
            </div>
          ) : (
            <>
              <StationList stations={data?.stations || []} fuel={fuel} limit={showAll ? 30 : 12} testIdPrefix="city-station" />
              {(data?.stations?.length || 0) > 12 && !showAll && (
                <button onClick={() => setShowAll(true)} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-5 py-2.5 text-[13px] font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors" data-testid="show-all-stations-btn">
                  Alle {Math.min(data.stations.length, 30)} Tankstellen anzeigen <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              )}
            </>
          )}
          <div className="mt-4">
            <DataAttribution fetchedAt={data?.fetched_at} />
          </div>
        </div>

        {/* Google Maps */}
        {MAPS_KEY && (
          <div className="mt-12">
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-5">
              Tankstellen-Karte {name}
            </h2>
            <iframe
              title={`Karte: Tankstellen in ${name}`}
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/search?key=${MAPS_KEY}&q=${encodeURIComponent(`Tankstellen in ${name}`)}&center=${city.lat},${city.lng}&zoom=12&language=de`}
              className="w-full h-[380px] sm:h-[440px] rounded-2xl border border-stone-200"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="city-map"
            />
          </div>
        )}

        {/* Ratgeber-Content */}
        <div className="mt-14 space-y-10 text-[15.5px] text-stone-600 leading-[1.75]">
          <section data-testid="city-guide-section">
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-4">
              Günstig tanken in {name}: So sparst du am meisten
            </h2>
            {spreadCt !== null && spreadCt > 0 && (
              <p className="mb-4">
                Der Preisunterschied zwischen der günstigsten und der teuersten Tankstelle in {name} beträgt aktuell{' '}
                <strong className="text-stone-900">{spreadCt} Cent pro Liter {fuelLabel}</strong>. Bei einer 50-Liter-Tankfüllung macht das bis zu{' '}
                <strong className="text-emerald-700">{(spreadCt * 0.5).toFixed(2).replace('.', ',')} €</strong> Unterschied — allein durch die Wahl der richtigen Tankstelle.
              </p>
            )}
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-stone-900">Abends tanken:</strong> Zwischen 18 und 22 Uhr sind die Preise laut MTS-K-Auswertungen im Tagesverlauf am niedrigsten — morgens zwischen 6 und 9 Uhr am höchsten. <Link to="/ratgeber/wann-tanken-am-guenstigsten" className="font-semibold text-emerald-700 hover:text-emerald-900">Zur besten Tankzeit</Link>.</li>
              <li><strong className="text-stone-900">Autobahn meiden:</strong> Tankstellen an Autobahnen sind oft 20–40 Cent teurer als Stationen im Stadtgebiet von {name}.</li>
              <li><strong className="text-stone-900">Preise live vergleichen:</strong> Diese Seite zeigt die amtlichen Live-Preise — ein kurzer Blick vor dem Tanken lohnt sich fast immer.</li>
              <li><strong className="text-stone-900">Kosten kalkulieren:</strong> Was deine Strecken bei den heutigen Preisen kosten, zeigt der <Link to="/spritkostenrechner" className="font-semibold text-emerald-700 hover:text-emerald-900">Spritkostenrechner</Link>.</li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-2.5" data-testid="city-cluster-links">
              <Link to={`/spritpreise/${slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-[13px] font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors" data-testid={`cluster-link-spritpreise-${slug}`}>
                Spritpreise {name} im Detail <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
              <Link to={`/dieselpreise/${slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-[13px] font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors" data-testid={`cluster-link-dieselpreise-${slug}`}>
                Dieselpreise {name} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </section>

          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 sm:p-7" data-testid="city-fuelx-box">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-700 mb-2.5">So hilft FuelX</p>
            <h3 className="font-display text-xl tracking-[-0.015em] text-stone-900 mb-3">Noch mehr sparen — an 150.000+ Tankstellen</h3>
            <div className="text-[15px] text-stone-700 leading-[1.7]">
              <p>
                Mit der FuelX Card sparst du bei aktiver Nutzung qualifizierender Angebote effektiv <strong>Ø 0,40 € pro Liter</strong> — zusätzlich zum Preisvergleich. Dazu kommen 37 Auto-Leistungen wie TÜV-, Kfz-Steuer- und Werkstatt-Vorteile, alle zentral an einem Ort verwaltet.
              </p>
            </div>
            <Link to="/fuelx-card" className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-emerald-700 hover:text-emerald-900 transition-colors" data-testid="city-fuelx-cta">
              FuelX Card entdecken <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>

          <section data-testid="city-faq-section">
            <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.02em] text-stone-900 mb-4">Häufige Fragen zu Tankstellen in {name}</h2>
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-stone-900 mb-1.5">Wie aktuell sind die Spritpreise für {name}?</h3>
                <p>Die Preise stammen direkt aus der Markttransparenzstelle für Kraftstoffe (MTS-K), an die alle Tankstellen in Deutschland ihre Preise in Echtzeit melden müssen. Diese Seite aktualisiert die Daten alle 15 Minuten.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1.5">Wann ist Tanken in {name} am günstigsten?</h3>
                <p>In der Regel abends zwischen 18 und 22 Uhr. Die Preise ändern sich mehrmals täglich — der Vergleich direkt vor dem Tanken bringt die größte Ersparnis.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1.5">Welche Kraftstoffe werden verglichen?</h3>
                <p>Super E5, Super E10 und Diesel — die drei meistgetankten Kraftstoffe in Deutschland. Der günstigste Preis pro Sorte wird oben hervorgehoben.</p>
              </div>
            </div>
          </section>
        </div>

        {/* Nachbarstädte */}
        {(data?.neighbors?.length || 0) > 0 && (
          <div className="mt-14 border-t border-stone-200 pt-8" data-testid="neighbor-cities">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-400 mb-4">Tankstellen in der Umgebung</p>
            <div className="flex flex-wrap gap-2.5">
              {data.neighbors.map((n) => (
                <Link
                  key={n.slug}
                  to={`/tankstellen/${n.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-4 py-2 text-[13px] font-medium text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors"
                  data-testid={`neighbor-link-${n.slug}`}
                >
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {n.name} <span className="text-stone-400">({n.dist_km} km)</span>
                </Link>
              ))}
              <Link to="/tankstellen" className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 text-white px-4 py-2 text-[13px] font-medium hover:bg-stone-800 transition-colors">
                Alle 500 Städte <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
        )}

        <p className="mt-10 flex items-center gap-1.5 text-[11px] text-stone-400">
          <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> FuelX ist keine Tankstelle, sondern eine unabhängige Autoplattform. Preisangaben ohne Gewähr.
        </p>
      </div>

      <CTABanner
        variant="light"
        headline={<>Tanken ist nur der Anfang.<br /><span className="text-stone-400">Alle Autokosten. Eine Card.</span></>}
        text="Mit der FuelX Card sparst du effektiv Ø 0,40 € pro Liter und regelst Versicherung, TÜV, Werkstatt & Termine an einem Ort."
        buttonText="FuelX Card entdecken"
        buttonTo="/fuelx-card"
      />
    </div>
  );
}
